import { Button } from '@Pages/pageRuntime/components/ui/Button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@Pages/pageRuntime/components/ui/dialog/AlertDialog';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

/**
 * `false` keeps the dialog open, so the page can show why the action failed.
 */
type ConfirmResult = void | boolean;

interface ConfirmDialogProps {
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => ConfirmResult | Promise<ConfirmResult>;
  children?: ReactNode;
  processing?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  variant?: 'default' | 'destructive';
  cancelLabel?: string;
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  onConfirm,
  children,
  processing,
  isOpen,
  onOpenChange,
  variant,
  cancelLabel = 'Cancel'
}: ConfirmDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [localProcessing, setLocalProcessing] = useState(false);
  const [waitingForExternalProcessing, setWaitingForExternalProcessing] = useState(false);
  const sawExternalProcessing = useRef(false);
  const isControlled = isOpen !== undefined;
  const open = isOpen ?? internalOpen;
  const hasExternalProcessing = processing !== undefined;
  const isProcessing = Boolean(processing) || localProcessing;
  const resolvedVariant =
    variant ?? (isDestructiveConfirmation(title, confirmLabel) ? 'destructive' : 'default');

  const setOpen = useCallback(
    (open: boolean) => {
      if (!open && isProcessing) {
        return;
      }

      if (isControlled) {
        onOpenChange?.(open);
      } else {
        setInternalOpen(open);
      }

      if (!open) {
        setWaitingForExternalProcessing(false);
        sawExternalProcessing.current = false;
      }
    },
    [isControlled, isProcessing, onOpenChange]
  );

  async function handleConfirm() {
    if (isProcessing) {
      return;
    }

    const result = onConfirm();

    if (isPromiseLike(result)) {
      setLocalProcessing(true);
      const settled = await Promise.resolve(result).finally(() => setLocalProcessing(false));

      if (settled !== false) {
        setOpen(false);
      }
      return;
    }

    if (result === false) {
      return;
    }

    if (hasExternalProcessing) {
      setWaitingForExternalProcessing(true);
      return;
    }

    setOpen(false);
  }

  useEffect(() => {
    if (!waitingForExternalProcessing || !hasExternalProcessing) {
      return;
    }

    if (processing) {
      sawExternalProcessing.current = true;
      return;
    }

    if (!sawExternalProcessing.current) {
      return;
    }

    sawExternalProcessing.current = false;
    setWaitingForExternalProcessing(false);
    setOpen(false);
  }, [hasExternalProcessing, processing, setOpen, waitingForExternalProcessing]);

  const dialogContent = (
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter
        start={<AlertDialogCancel disabled={isProcessing}>{cancelLabel}</AlertDialogCancel>}
      >
        <Button
          type="button"
          variant={resolvedVariant}
          loading={isProcessing}
          onClick={() => void handleConfirm()}
        >
          {confirmLabel}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {children ? <AlertDialogTrigger asChild>{children}</AlertDialogTrigger> : null}
      {dialogContent}
    </AlertDialog>
  );
}

function isDestructiveConfirmation(title: ReactNode, confirmLabel: ReactNode): boolean {
  const text = [title, confirmLabel]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();

  return /\b(delete|remove)\b/.test(text);
}

function isPromiseLike(
  value: ConfirmResult | Promise<ConfirmResult>
): value is Promise<ConfirmResult> {
  return typeof value === 'object' && value !== null && typeof value.then === 'function';
}
